import lessonSchema from './lesson.schema.json' with { type: 'json' };
import { QUIZ_LEVELS, SKELETON, assessPredicting, isExplaining, rangeWords, } from './lesson-length.js';
/**
 * Lesson phases (ADR-062; docs/superpowers/specs/2026-10-13-lesson-phases-design.md): where the teacher
 * places a generated lesson in the theme ("Waar in je les?"), the four phases a teacher knows (Wat is
 * dit?, Onderzoeken, Meer weten, Afsluiting) as a skeleton for each kind and length, and the rules code
 * prompt v8's checks hold a design and a lesson to. Pure; the words and problems are Dutch, as the checks
 * show them and a Herstel prompt carries them. The internal parts (`LESSON_PARTS`) keep their ids: the
 * phases are a grouping and a set of labels on top of them.
 */
/** Where in the theme the lesson sits: the opening, deepening after the class explanation, review. */
export const LESSON_PLACES = ['begin', 'verdieping', 'herhaling'];
/** Every brief without an answer, platform briefs and those of before included. */
export const DEFAULT_LESSON_PLACE = 'verdieping';
export const isLessonPlace = (value) => typeof value === 'string' && LESSON_PLACES.includes(value);
/** A brief's `lessonPlace` answer as a kind: Verdieping when it has none (or one it should not have). */
export function lessonPlace(value) {
    return isLessonPlace(value) ? value : DEFAULT_LESSON_PLACE;
}
/** The kinds by name, as the console offers them and a v8 prompt's brief says them. */
export const PLACE_WORDS = {
    begin: 'Begin van het thema',
    verdieping: 'Verdieping',
    herhaling: 'Herhaling',
};
/** The lengths each kind is offered at: a Herhaling is short. */
export const PLACE_LENGTHS = {
    begin: [10, 20, 30],
    verdieping: [10, 20, 30],
    herhaling: [10, 20],
};
const h = 'herkennen';
const b = 'begrijpen';
const t = 'toepassen';
/**
 * Every kind's counts (spec §2, at the top of each range). Verdieping is ADR-055's skeleton under the
 * new names: its counts (`SKELETON`) and late predicting are unchanged. A Meer weten step plans 70
 * words and a question 40, and an Onderzoeken step 5 points (6 in 30 minutes), unless a kind needs more
 * to plan 95–105 % of its length (lesson-phases.test.ts): Begin van het thema from 20 minutes 80 words;
 * Herhaling 80 words, its questions 45, and 6 points in 20 minutes.
 */
export const PHASES = {
    begin: {
        10: {
            rounds: 2,
            extra: false,
            early: 1,
            late: [],
            levels: [h, b, t],
            pause: false,
            points: 5,
            explainWords: 70,
            questionWords: 40,
            explain: [4, 4],
            predict: [1, 1],
            questions: [3, 3],
        },
        20: {
            rounds: 5,
            extra: false,
            early: 1,
            late: [],
            levels: [h, b, t, t],
            pause: true,
            points: 5,
            explainWords: 80,
            questionWords: 40,
            explain: [8, 10],
            predict: [1, 1],
            questions: [3, 4],
        },
        30: {
            rounds: 7,
            extra: false,
            early: 1,
            late: [5],
            levels: [h, b, b, t, t],
            pause: true,
            points: 6,
            explainWords: 80,
            questionWords: 40,
            explain: [12, 14],
            predict: [1, 2],
            questions: [4, 5],
        },
    },
    verdieping: {
        10: {
            rounds: 2,
            extra: true,
            early: 0,
            late: [],
            levels: [h, b, t],
            pause: false,
            points: 5,
            explainWords: 70,
            questionWords: 40,
            explain: [4, 5],
            predict: [0, 0],
            questions: [3, 3],
        },
        20: {
            rounds: 5,
            extra: false,
            early: 0,
            late: [3],
            levels: [h, h, b, t, t],
            pause: true,
            points: 5,
            explainWords: 70,
            questionWords: 40,
            explain: [8, 10],
            predict: [1, 1],
            questions: [4, 5],
        },
        30: {
            rounds: 7,
            extra: true,
            early: 0,
            late: [4, 6],
            levels: [h, h, b, b, t, t],
            pause: true,
            points: 6,
            explainWords: 70,
            questionWords: 40,
            explain: [12, 15],
            predict: [1, 2],
            questions: [5, 6],
        },
    },
    herhaling: {
        10: {
            rounds: 1,
            extra: true,
            early: 0,
            late: [],
            levels: [h, h, b, t, t],
            pause: false,
            points: 5,
            explainWords: 80,
            questionWords: 45,
            explain: [3, 3],
            predict: [0, 0],
            questions: [4, 5],
        },
        20: {
            rounds: 3,
            extra: true,
            early: 0,
            late: [],
            levels: [h, h, h, b, b, t, t, t],
            pause: true,
            points: 6,
            explainWords: 80,
            questionWords: 45,
            explain: [6, 7],
            predict: [0, 0],
            questions: [7, 8],
        },
    },
};
/**
 * "Eén ding van dichtbij" ("Wat wil je maken?" plan 4), 10 minutes whatever "Waar in je les?" says: Wat is
 * dit?, four Onderzoeken steps in a row -- the one model at its spots A, B, C and D, one side each, 4
 * points a side -- one Meer weten in which it moves, and the doordenkvraag; no prediction, no pause. A
 * Meer weten step plans 80 words: the skeleton plans 93 % of its 10 minutes (lesson-phases.test.ts).
 */
export const DICHTBIJ_PHASES = {
    rounds: 1,
    sides: 4,
    extra: false,
    early: 0,
    late: [],
    levels: [t],
    pause: false,
    points: 4,
    explainWords: 80,
    questionWords: 40,
    explain: [5, 5],
    predict: [0, 0],
    questions: [1, 1],
    name: 'Eén ding van dichtbij',
};
/** The counts of `place` at `length`; a Herhaling is never longer than 20 minutes. */
export function phasesOf(length, place) {
    return PHASES[place][length] ?? PHASES[place][20];
}
/**
 * An Echte plek lesson's hotspots a round (Pieter, 22 Oct 2026): every Onderzoeken step looks at the
 * place's named points and the lesson visits each at least once, so a round asks no more than the place
 * has (a point comes back in a later round with a new question) and the rounds together at least as many
 * as it has, up to a step's STEP_HOTSPOTS_MAX: the points the rounds have no room for (a 10-minute
 * Herhaling's one round at 7 or 8 points) are pointed at in the Meer weten steps, one each. A longer
 * lesson goes deeper per point.
 */
export function placeRoundPoints(counts, placePoints) {
    return Math.min(STEP_HOTSPOTS_MAX, Math.max(Math.min(counts.points, placePoints), Math.ceil(placePoints / counts.rounds)));
}
/** The most hotspots a step may have (the schema's `maxItems`). */
export const STEP_HOTSPOTS_MAX = lessonSchema.definitions.step.properties.hotspots.maxItems;
/** What the other slots of a v8 design plan to say, in words (`Vertelling: n woorden`). */
export const PHASE_WORDS = { opening: 60, verkennen: 60, voorspellen: 40 };
/**
 * The skeleton a v8 design is given (spec §1–2): Wat is dit?; a prediction right after it for Begin van
 * het thema; the rounds, each Onderzoeken then Meer weten, a late prediction inside its round; one more
 * Meer weten where the kind has it, the last marked for the pause from 20 minutes; the Afsluiting, its
 * questions rising, the doordenkvraag last. Each slot carries its planned words.
 */
export function phaseSkeleton(length, place, 
/** An Echte plek lesson's hotspots a round (`placeRoundPoints`); absent, the kind's. */
roundPoints, 
/** A kind's own counts (`DICHTBIJ_PHASES`); absent, `place`'s at `length`. */
kind) {
    const counts = kind ?? phasesOf(length, place);
    const points = roundPoints ?? counts.points;
    const explain = () => ({ part: 'uitleg', words: counts.explainWords });
    const predict = () => ({ part: 'voorspellen', words: PHASE_WORDS.voorspellen });
    const slots = [{ part: 'opening', words: PHASE_WORDS.opening }];
    for (let i = 0; i < counts.early; i++)
        slots.push(predict());
    for (let round = 1; round <= counts.rounds; round++) {
        for (let side = 0; side < (counts.sides ?? 1); side++) {
            slots.push({ part: 'verkennen', points, words: PHASE_WORDS.verkennen });
        }
        if (counts.late.includes(round))
            slots.push(predict());
        slots.push(explain());
    }
    if (counts.extra)
        slots.push(explain());
    if (counts.pause) {
        const last = slots.length - 1;
        slots[last] = { ...slots[last], pause: true };
    }
    counts.levels.forEach((level, i) => {
        const doordenk = i === counts.levels.length - 1;
        slots.push({
            part: 'vraag',
            level: doordenk ? 'toepassen' : level,
            words: counts.questionWords,
            ...(doordenk ? { doordenk: true } : {}),
        });
    });
    return slots;
}
/** The phases by name, as a v8 design labels its steps and "Dit krijg je" groups them. */
export const PHASE_NAMES = {
    opening: 'Wat is dit?',
    verkennen: 'Onderzoeken',
    uitleg: 'Meer weten',
    voorspellen: 'Voorspellen',
    afsluiting: 'Afsluiting',
    doordenk: 'Doordenkvraag',
};
/**
 * A slot as a v8 design labels its step: `[Wat is dit?]`, `[Onderzoeken: 5 punten]`, `[Meer weten]`,
 * `[Voorspellen]`, `[Vraag: herkennen]`, `[Doordenkvraag]`.
 */
export function phaseLabel(slot) {
    if (slot.part === 'opening')
        return `[${PHASE_NAMES.opening}]`;
    if (slot.part === 'verkennen')
        return `[${PHASE_NAMES.verkennen}: ${slot.points ?? 2} punten]`;
    if (slot.part === 'uitleg')
        return `[${PHASE_NAMES.uitleg}]`;
    if (slot.part === 'voorspellen')
        return `[${PHASE_NAMES.voorspellen}]`;
    if (slot.doordenk === true)
        return `[${PHASE_NAMES.doordenk}]`;
    return `[Vraag: ${slot.level ?? QUIZ_LEVELS[0]}]`;
}
const within = (n, [min, max]) => n >= min && n <= max;
/**
 * "Volgt de opbouw" (spec §7): Wat is dit? first and only once; then rounds, never two Onderzoeken steps
 * in a row nor three Meer weten steps (a prediction between them not counted); the Afsluiting together
 * at the end; Onderzoeken and Meer weten held to the kind's minimum only ("Les vult de tijd" holds the
 * length); the questions to the kind's range; the pause as ADR-055 has it. Predicting is
 * `assessPredictPlace`'s to say, a question's level `assessQuestionLevels`', the doordenkvraag
 * `assessDoordenk`'s: one problem for one mistake.
 */
export function assessPhases(slots, length, place, options = {}) {
    const hint = options.lesson === true ? ' (een stap met twee of meer `hotspots` telt als Onderzoeken)' : '';
    if (slots.length === 0)
        return ['geen stappen gevonden'];
    const counts = options.counts ?? phasesOf(length, place);
    const kind = `${length} minuten (${counts.name ?? PLACE_WORDS[place]})`;
    const problems = [];
    if (slots[0].part !== 'opening') {
        problems.push('stap 1 is geen Wat is dit? (waar zijn we, wat ga je ontdekken)');
    }
    const openings = slots.filter((slot) => slot.part === 'opening').length;
    if (openings > 1)
        problems.push(`${openings} keer Wat is dit?: alleen stap 1 is Wat is dit?`);
    const explaining = slots.flatMap((slot, i) => isExplaining(slot) ? [{ part: slot.part, i }] : []);
    if (explaining.length < counts.explain[0]) {
        problems.push(`${explaining.length} Onderzoeken- en Meer weten-stappen, voor ${kind} minstens ${counts.explain[0]}`);
    }
    const questions = slots.filter((slot) => slot.part === 'vraag').length;
    if (!within(questions, counts.questions)) {
        problems.push(`${questions} vragen, voor ${kind} ${rangeWords(counts.questions)}`);
    }
    const first = slots.findIndex((slot) => slot.part === 'vraag');
    if (first >= 0) {
        const stray = slots.slice(first).findIndex((slot) => slot.part !== 'vraag');
        if (stray >= 0) {
            problems.push(`stap ${first + stray + 1} komt na de eerste vraag (stap ${first + 1}): de Afsluiting komt samen aan het eind`);
        }
    }
    let explained = 0;
    let explored = 0;
    const sides = counts.sides ?? 1;
    for (const { part, i } of explaining) {
        explored = part === 'verkennen' ? explored + 1 : 0;
        if (explored > sides) {
            problems.push(sides === 1
                ? `stap ${i + 1}: twee Onderzoeken-stappen na elkaar; na Onderzoeken komt Meer weten${hint}`
                : `stap ${i + 1}: ${explored} Onderzoeken-stappen na elkaar; na ${sides} komt Meer weten${hint}`);
            break;
        }
        explained = part === 'uitleg' ? explained + 1 : 0;
        if (explained === 3) {
            problems.push(`stap ${i + 1}: drie Meer weten-stappen na elkaar; wissel Onderzoeken en Meer weten af${hint}`);
            break;
        }
    }
    const pauses = slots.flatMap((slot, i) => (slot.pause === true ? [i + 1] : []));
    const last = explaining.at(-1);
    if (length === 10) {
        if (pauses.length > 0) {
            problems.push(`een les van 10 minuten heeft geen pauze: haal de pauze bij stap ${pauses.join(', ')} weg`);
        }
    }
    else if (last !== undefined && (pauses.length !== 1 || pauses[0] !== last.i + 1)) {
        problems.push(`zet de pauze op stap ${last.i + 1}, de laatste Meer weten- of Onderzoeken-stap${pauses.length > 0 ? ` (nu bij stap ${pauses.join(', ')})` : ''}`);
    }
    return problems;
}
/**
 * "Voorspellen op de goede plek" (spec §7). Begin van het thema: one prediction at step 2, right after
 * Wat is dit?, and in 30 minutes at most one more after half of the Onderzoeken and Meer weten steps.
 * Verdieping: ADR-055's rule (none in 10 minutes, after half, the count of the length). Herhaling: none.
 */
export function assessPredictPlace(slots, length, place, 
/** A kind's own counts (`DICHTBIJ_PHASES`): with none to make, it predicts nowhere. */
kind) {
    const predicts = slots.flatMap((slot, i) => (slot.part === 'voorspellen' ? [i] : []));
    const steps = (list) => list.map((i) => i + 1).join(', ');
    if (kind !== undefined && kind.predict[1] === 0) {
        return predicts.length === 0
            ? []
            : [`${kind.name ?? 'deze les'} voorspelt niet: maak van stap ${steps(predicts)} Meer weten`];
    }
    if (place === 'herhaling') {
        return predicts.length === 0
            ? []
            : [`een herhaling voorspelt niet: maak van stap ${steps(predicts)} Meer weten`];
    }
    if (place === 'verdieping') {
        if (length === 10) {
            return predicts.length === 0
                ? []
                : [
                    `een les van 10 minuten (Verdieping) voorspelt niet: maak van stap ${steps(predicts)} Meer weten`,
                ];
        }
        const counts = SKELETON[length];
        return [
            ...(within(predicts.length, counts.predict)
                ? []
                : [
                    `${predicts.length} voorspelstappen, voor ${length} minuten ${rangeWords(counts.predict)}`,
                ]),
            ...assessPredicting(slots, length),
        ];
    }
    const counts = phasesOf(length, place);
    const problems = [];
    if (!predicts.includes(1)) {
        problems.push('een les die het thema begint, voorspelt in stap 2, meteen na Wat is dit?: de leerling voorspelt voordat hij iets weet');
    }
    const later = predicts.filter((i) => i !== 1);
    const allowed = counts.predict[1] - 1;
    const total = slots.filter(isExplaining).length;
    const needed = Math.floor(total / 2);
    later.forEach((i, n) => {
        if (n >= allowed) {
            problems.push(allowed === 0
                ? `stap ${i + 1} voorspelt: een les die het thema begint, voorspelt alleen in stap 2`
                : `stap ${i + 1} voorspelt: een les die het thema begint, voorspelt in stap 2 en hooguit één keer na de helft`);
            return;
        }
        const before = slots.slice(0, i).filter(isExplaining).length;
        if (before < needed) {
            problems.push(`stap ${i + 1} voorspelt na ${before} van de ${total} Onderzoeken- en Meer weten-stappen: pas na de helft (${needed})`);
        }
    });
    return problems;
}
/**
 * "Eindigt met een doordenkvraag" (spec §4, §7): the last question is the doordenkvraag (`doordenk:
 * true`, `[Doordenkvraag]` in a design), at level toepassen, and no other question is. That it is the
 * last step and answered from memory stays the existing checks'.
 */
export function assessDoordenk(slots, options = {}) {
    const questions = slots.flatMap((slot, i) => (slot.part === 'vraag' ? [{ slot, i }] : []));
    const last = questions.at(-1);
    if (last === undefined)
        return ['geen vragen gevonden: de les eindigt met een doordenkvraag'];
    const problems = [];
    const others = questions.filter((q) => q !== last && q.slot.doordenk === true);
    if (others.length > 0) {
        problems.push(`stap ${others.map((q) => q.i + 1).join(', ')}: alleen de laatste vraag is de doordenkvraag`);
    }
    if (last.slot.doordenk !== true) {
        problems.push(`stap ${last.i + 1}, de laatste vraag, is de doordenkvraag: ${options.design === true
            ? 'begin hem met [Doordenkvraag]'
            : 'geef hem "doordenk": true en niveau toepassen'}`);
    }
    else if (last.slot.level !== 'toepassen') {
        problems.push(`stap ${last.i + 1}: de doordenkvraag heeft niveau toepassen`);
    }
    return problems;
}
//# sourceMappingURL=lesson-phases.js.map